import type { QuestDefinition } from './quests'
import type { QuestId, QuestPickState } from './types'
import type { TeamId } from './teams'

/**
 * The graded truth of one quest in one gameweek, and the rules that read it.
 *
 * **Shared, not client code**, like `src/quests.ts`: server and client must answer "did this pick
 * hit?" from one function rather than two copies that drift. The server owns the *points*.
 *
 * Nothing here is invented — a `QuestResult` comes from `server/questScoring.ts` and is persisted
 * once per (gameweek, quest).
 */

export type QuestOutcome = 'hit' | 'miss' | 'unplayed' | 'void'

/**
 * What a correct quest is worth — **+2 points**. Two quests run per gameweek, so +4 is the weekly
 * maximum, folded into the gameweek total rather than tracked separately.
 *
 * (`mockQuestResults.MOCK_QUEST_POINTS` applied 4 to a *single* quest — the weekly maximum drawn on
 * one example card. It was never the rule.)
 *
 * - **A wrong quest costs nothing.** 0, never negative: no other mechanic in the app subtracts.
 * - **Every quest type is worth the same.** Per-type values would need a difficulty model nobody has.
 */
export const QUEST_POINTS = 2

/** The most quests can add to one gameweek: both of the round's pair, correct. */
export const QUEST_POINTS_PER_GAMEWEEK = QUEST_POINTS * 2

/** Whether the quest was gradeable at all — see `VOID_*` reasons in server/questScoring.ts. */
export type QuestResultStatus = 'graded' | 'void'

/**
 * One quest's graded truth. Persisted verbatim as the `quest_results.payload` JSON and served to
 * the client unchanged, so what the card renders is the row that was graded — not a re-derivation
 * that a late provider revision could quietly rewrite a week later.
 */
export interface QuestResult {
  gameweek: number
  questId: QuestId
  status: QuestResultStatus
  /** Operator-facing, never rendered: why grading was impossible. Only set when voided. */
  voidReason?: string
  /**
   * The teams that satisfy a team quest, best first. **Joint winners all count** — see
   * `questOutcomeFor`. Empty when nothing qualified (e.g. no team led at half-time).
   */
  winningTeams?: TeamId[]
  /** The fixtures that satisfy a match quest, best first. Same tie rule. */
  winningGameIds?: string[]
  /** The side that hit, for an over/under quest. */
  trueSide?: 'over' | 'under'
  /** The figure behind the answer: the winning stat, or the over/under's own count. */
  value?: number
  /**
   * Every entrant's figure, keyed by team id (team quests) or fixture id (match quests). What lets
   * a miss show the user their own number beside the winning one.
   */
  figures?: Record<string, number>
  /**
   * The result is real but **not everyone's yet**.
   *
   * A fixture-scoped quest settles per fixture, at half-time, while the rest of the round is still
   * being played — so its `figures` hold the entrants whose answer is final and nobody else. An
   * entrant absent from a partial result has not been decided; `questOutcomeFor` returns
   * `undefined` for them, keeping their card sealed rather than reading the absence as a miss.
   *
   * Absent (or `false`) on every round-scoped result, which covers the whole round by construction.
   */
  partial?: boolean
}

/** The unit each quest's figures are counted in — display only. */
export const QUEST_UNIT: Record<QuestId, string> = {
  busiestKeeper: 'saves',
  bookingBandits: 'cards',
  frontRunners: 'up at HT',
  cornerStorm: 'corners',
  secondHalfSurge: '2nd-half goals',
  firstHalfAction: '1st-half goals',
  spotKickLine: 'penalties',
  braceBracket: 'braces',
}

/** `"9 saves"` — the one place a figure and its unit are joined. */
export function questFigureLine(questId: QuestId, value: number | undefined): string | undefined {
  return value === undefined ? undefined : `${value} ${QUEST_UNIT[questId]}`
}

/**
 * Miss copy for the quests answered per pick rather than by a race.
 *
 * Neither half-time quest has a winner — six or seven clubs hit a round — so their `value` is a
 * stranger's best figure. Showing that as the answer the player missed invents a race that was
 * never run, so a miss shows no number. Quests absent from this map keep the winning figure.
 */
export const QUEST_MISS_VERDICT: Partial<Record<QuestId, string>> = {
  frontRunners: 'Not ahead at HT',
  firstHalfAction: 'Under 1.5 at HT',
}

/** Whether the player actually made this quest's pick (its type's own field). */
export function hasQuestPickValue(
  quest: QuestDefinition,
  pick: QuestPickState | undefined
): boolean {
  return quest.type === 'team'
    ? !!pick?.team
    : quest.type === 'match'
      ? !!pick?.gameId
      : !!pick?.side
}

/** Whether a partial result has already decided this pick — i.e. its entrant carries a figure. */
function partialCoversPick(
  quest: QuestDefinition,
  result: QuestResult,
  pick: QuestPickState | undefined
): boolean {
  if (!hasQuestPickValue(quest, pick)) return false
  const entrant = quest.type === 'team' ? pick!.team : quest.type === 'match' ? pick!.gameId : undefined
  // An over/under quest counts a round total, so half a round never decides one — no entrant key
  // exists to look up, and the answer is simply not ready.
  return entrant !== undefined && entrant in (result.figures ?? {})
}

/**
 * The one definition of hit/miss, used by the server for points and by the card for its verdict.
 *
 * **A void wins**: a voided quest awards nothing to anyone, because the data to judge it does not
 * exist. A missing result is *not* a void — it is ungraded, so callers keep the sealed state.
 * Hence `undefined` in, `undefined` out.
 *
 * **Ties all win.** "Most corners" ties often, and a tiebreak would either invent a rule the card
 * cannot state or punish a correct read.
 */
export function questOutcomeFor(
  quest: QuestDefinition,
  result: QuestResult | undefined,
  pick: QuestPickState | undefined
): QuestOutcome | undefined {
  if (!result) return undefined
  if (result.status === 'void') return 'void'
  // Half-graded: the entrants that settled have their verdict, everyone else is
  // still pending. Reading a partial result as complete would tell a user they lost a quest whose
  // fixture has not kicked off, and would show an "unplayed" card a true answer that can still
  // change — so both stay `undefined` until the entrant is in.
  if (result.partial === true && !partialCoversPick(quest, result, pick)) return undefined
  if (!hasQuestPickValue(quest, pick)) return 'unplayed'

  if (quest.type === 'team') {
    return (result.winningTeams ?? []).includes(pick!.team as TeamId) ? 'hit' : 'miss'
  }
  if (quest.type === 'match') {
    return (result.winningGameIds ?? []).includes(pick!.gameId as string) ? 'hit' : 'miss'
  }
  return pick!.side === result.trueSide ? 'hit' : 'miss'
}

/** What one quest paid this player: +2 for a hit, 0 for everything else. */
export function questPointsFor(
  quest: QuestDefinition,
  result: QuestResult | undefined,
  pick: QuestPickState | undefined
): number {
  return questOutcomeFor(quest, result, pick) === 'hit' ? QUEST_POINTS : 0
}

/** The gameweek's quest points for one player — 0 through `QUEST_POINTS_PER_GAMEWEEK`. */
export function questPointsForGameweek(
  quests: readonly QuestDefinition[],
  results: Partial<Record<QuestId, QuestResult>>,
  picks: Partial<Record<QuestId, QuestPickState>>
): number {
  return quests.reduce(
    (sum, quest) => sum + questPointsFor(quest, results[quest.id], picks[quest.id]),
    0
  )
}
